// Ski v3 aggregation: class-conditional quality, cluster -> band -> resort, the resort
// extent factor X, day roll-ups, member quantiles, resort status, labels and risk flags.
// Contract: docs/features/ski-scoring-v3.md Sections 14 and 16 (rationale in the deep
// dive, Sections 5.8 and 5.9).
//
// The engine calls these per hour and per member, then per day. Every function is a
// pure function of its arguments: no I/O, no clock, no randomness, no allocation that
// depends on anything but the inputs. Scores are on the 0 to 100 scale (s = 100 G Q W).
//
// Charge-once notes:
// - Limited open terrain acts through the extent factor X here (variety) and through
//   traffic concentration upstream (physical wear). Nothing in this file lowers quality
//   for low extent.
// - Uncertainty about which usability class applies (equipped, operating) moves extent,
//   never quality: class-conditional quality keeps the usable classes' quality.
//
// Smoothness notes: the soft-max and the top-share mean are continuous in their inputs;
// the switch from modelled to observed extent is a smoothstep blend over coverage; the
// conditional quality tends smoothly to the unconditional expectation as the open
// probability tends to zero (a tiny pseudo-count, never a hard switch). Status, labels
// and risk flags are categorical outputs by contract and use plain thresholds.
import { pisteLabel as pisteLabel10 } from "../piste-labels";
import { clamp as mathClamp, finite as mathFinite, smoothstep as mathSmoothstep, softMax as mathSoftMax } from "./math";
import { SKI_V3_PARAMS } from "./params";
import type { OffPisteStateName } from "./offpiste";
import type { PisteStateName } from "./surface";
import type { SkiSport, SkiStatus, StateProbability } from "./types";

// Hot-path bindings (performance pass, 27 Sep): a module runner or bundler can route each call to
// an imported function through a getter, which stops the hourly loops inlining it. Module-level
// copies are plain functions with the same behaviour.
const clamp = mathClamp;
const finite = mathFinite;
const smoothstep = mathSmoothstep;
const softMax = mathSoftMax;

const A = SKI_V3_PARAMS.aggregate;

/** Published labels that override the score vocabulary. */
export const SKI_LABEL_CLOSED = "Closed";
export const SKI_LABEL_NO_LIFT_ACCESS = "No lift access";
export const SKI_LABEL_UNAVAILABLE = "Score unavailable";

export type ExtentBasis = "observed" | "modelled" | "prior";

/**
 * Piste states that count towards the ice-risk flag: ice and glaze (`ice`, `steep_ice`)
 * and boilerplate (hard scraped `polished`, refrozen `frozen_chop`).
 */
export const ICE_RISK_PISTE_STATES: readonly PisteStateName[] = ["ice", "steep_ice", "polished", "frozen_chop"];
/** Natural-snow analogue of refrozen chop, so the flag also works on off-piste state lists. */
export const ICE_RISK_OFFPISTE_STATES: readonly OffPisteStateName[] = ["refrozen_chunder"];
/** Every state name iceRisk sums (piste and off-piste names do not collide on these). */
export const ICE_RISK_STATES: readonly string[] = [...ICE_RISK_PISTE_STATES, ...ICE_RISK_OFFPISTE_STATES];
const ICE_RISK_SET: ReadonlySet<string> = new Set<string>(ICE_RISK_STATES);

// ---------------------------------------------------------------------------
// Small sanitisers (invariant 8: a damaged input never produces NaN)
// ---------------------------------------------------------------------------

const orElse = (x: number | null | undefined, fallback: number): number => (finite(x) ? x : fallback);
/** A probability in [0, 1]; non-finite reads as 0. */
const prob = (x: number | null | undefined): number => clamp(orElse(x, 0));
const scored = (v: number | null | undefined): v is number => finite(v);
const intLen = (n: number, fallback: number): number => Math.max(1, Math.floor(orElse(n, fallback)));

// ---------------------------------------------------------------------------
// Class-conditional quality (contract Section 16)
// ---------------------------------------------------------------------------

export type UsabilityClassEntry = Readonly<{
  /** Class probability (normalised to sum to 1 across the entries). */
  pi: number;
  /** Usability (open or operating) probability of the class. */
  a: number;
  /** Hour score of the class, 0 to 100 (already includes G). */
  s: number;
}>;

/**
 * Class-conditional quality `q = sum pi a s / sum pi a` and extent `sum pi a`
 * (contract Section 16). Class probabilities are normalised to sum to 1 first (identical
 * for proper probabilities; equal shares when they are all zero). When the usability
 * mass is near zero, q tends smoothly to the unconditional expectation `sum pi s`
 * through a tiny pseudo-count, so the result is never NaN. Entries with a non-finite
 * score still count towards extent but not towards quality.
 */
export function classConditional(entries: readonly UsabilityClassEntry[]): { q: number; extent: number } {
  const n = entries.length;
  if (n === 0) return { q: 0, extent: 0 };
  let piSum = 0;
  for (const e of entries) piSum += Math.max(0, orElse(e.pi, 0));
  const equal = !(piSum > 0);
  let num = 0;
  let den = 0;
  let unc = 0;
  let uncW = 0;
  let extent = 0;
  for (const e of entries) {
    const pi = equal ? 1 / n : Math.max(0, orElse(e.pi, 0)) / piSum;
    const a = prob(e.a);
    extent += pi * a;
    if (!finite(e.s)) continue;
    num += pi * a * e.s;
    den += pi * a;
    unc += pi * e.s;
    uncW += pi;
  }
  if (!(uncW > 0)) return { q: 0, extent: clamp(extent) };
  const k = A.openPseudoCount;
  const q = (num + k * (unc / uncW)) / (den + k);
  return { q: finite(q) ? q : 0, extent: clamp(extent) };
}

/**
 * Cluster quality for one hour and member (contract Section 14):
 * `q = (e aE sE + (1 - e) aU sU)/(e aE + (1 - e) aU)` and `pOpen = e aE + (1 - e) aU`.
 * For an ungroomed cluster pass e = 0 with aU = a_g and sU = s_g. When pOpen is near 0
 * the quality is the unconditional expectation `e sE + (1 - e) sU` (never NaN).
 */
export function clusterHourQuality(a: { e: number; aE: number; sE: number; aU: number; sU: number }): { q: number; pOpen: number } {
  const e = prob(a.e);
  const r = classConditional([
    { pi: e, a: a.aE, s: a.sE },
    { pi: 1 - e, a: a.aU, s: a.sU },
  ]);
  return { q: r.q, pOpen: r.extent };
}

// ---------------------------------------------------------------------------
// Band and resort per hour (contract Section 14)
// ---------------------------------------------------------------------------

export type ClusterHour = Readonly<{
  /** Cluster quality, 0 to 100. */
  q: number;
  pOpen: number;
  lengthM: number;
  /** Traffic intensity after concentration (relative, median 1). */
  traffic: number;
}>;
export type ResortClusterHour = ClusterHour &
  Readonly<{
    mandatory: boolean;
    /**
     * Unconditional quality of a mandatory cluster (0 to 100): the class expectation including
     * classes that are not usable (thin or bare snow scores low through G). A mandatory run is
     * skied whether it is in good shape or not, so the resort mean uses this value when given;
     * the band soft-max keeps the class-conditional q.
     */
    qMandatory?: number;
  }>;

/** Soft-max with a weighted-mean limit for beta at or near 0; null when nothing is weighted. */
function softMaxOrMean(values: readonly number[], weights: readonly number[], beta: number): number | null {
  if (!values.length) return null;
  if (finite(beta) && beta > 1e-9) {
    const v = softMax(values, weights, beta);
    return finite(v) ? v : null;
  }
  let s = 0;
  let w = 0;
  for (let i = 0; i < values.length; i++) {
    const wi = weights[i];
    if (!(wi > 0)) continue;
    s += wi * values[i];
    w += wi;
  }
  return w > 0 ? s / w : null;
}

/** Soft-max quality over clusters with w = pOpen length traffic; null when no cluster is valid. */
function clusterSoftMax(clusters: readonly ClusterHour[], beta: number): number | null {
  const qs: number[] = [];
  const ws: number[] = [];
  const lens: number[] = [];
  for (const c of clusters) {
    const len = orElse(c.lengthM, 0);
    if (!(len > 0) || !finite(c.q)) continue;
    const traffic = Math.max(0, orElse(c.traffic, 1));
    qs.push(c.q);
    ws.push((prob(c.pOpen) + A.weightPseudoOpen) * len * traffic);
    lens.push(len);
  }
  // Zero traffic everywhere would weight nothing: fall back to length weights.
  return softMaxOrMean(qs, ws, beta) ?? softMaxOrMean(qs, lens, beta);
}

/** Length-weighted open fraction `sum length pOpen / sum length` (0 when no length). */
function lengthExtent(clusters: readonly ClusterHour[]): number {
  let len = 0;
  let open = 0;
  for (const c of clusters) {
    const l = orElse(c.lengthM, 0);
    if (!(l > 0)) continue;
    len += l;
    open += l * prob(c.pOpen);
  }
  return len > 0 ? clamp(open / len) : 0;
}

/**
 * Band piste quality for one hour and member: the soft-max
 * `(1/beta) ln(sum w e^(beta q)/sum w)` with `w = pOpen length traffic`, and the band
 * extent `sum length pOpen/sum length`. The soft-max lies between the lowest and
 * highest cluster quality and equals q exactly for a single cluster (parity invariant).
 * A band whose clusters are all shut keeps its length and traffic weighted "if open"
 * quality. An empty band gives score 0 and extent 0.
 */
export function bandPisteHour(clusters: readonly ClusterHour[], beta: number = A.clusterSoftMaxBeta): { score: number; extent: number } {
  return { score: clusterSoftMax(clusters, beta) ?? 0, extent: lengthExtent(clusters) };
}

/**
 * Resort piste quality for one hour and member:
 * `V = (1 - m) softmax(optional clusters) + m mean(mandatory clusters)`, with m = 0.12
 * when mandatory clusters exist and 0 otherwise. Mandatory clusters (links and home runs
 * that must be skied) enter linearly as a length-weighted mean, regardless of their open
 * probability, with their unconditional quality `qMandatory` when the caller supplies it
 * (tuned round 2: a bare home run that must be skied cannot be scored as if it were open).
 * With only mandatory clusters V is their mean. Extent covers all clusters.
 */
export function resortPisteHour(clusters: readonly ResortClusterHour[]): { score: number; extent: number } {
  const optional: ResortClusterHour[] = [];
  let mandSum = 0;
  let mandLen = 0;
  for (const c of clusters) {
    if (!c.mandatory) {
      optional.push(c);
      continue;
    }
    const len = orElse(c.lengthM, 0);
    const q = finite(c.qMandatory) ? c.qMandatory : c.q;
    if (!(len > 0) || !finite(q)) continue;
    mandSum += len * q;
    mandLen += len;
  }
  const opt = clusterSoftMax(optional, A.clusterSoftMaxBeta);
  const mand = mandLen > 0 ? mandSum / mandLen : null;
  let score: number;
  if (mand === null) score = opt ?? 0;
  else if (opt === null) score = mand;
  else score = (1 - A.mandatoryWeight) * opt + A.mandatoryWeight * mand;
  return { score, extent: lengthExtent(clusters) };
}

// ---------------------------------------------------------------------------
// Extent and the extent factor X (contract Section 14, owner decision)
// ---------------------------------------------------------------------------

/**
 * Weight of the observed open fraction against the modelled extent: a smoothstep over
 * coverage 0.5 to 0.7, one half at the 60 % threshold.
 */
export function observedExtentWeight(coverage: number): number {
  const lo = A.observedExtentMinCoverage - A.observedBlendHalfWidth;
  return smoothstep((prob(coverage) - lo) / (2 * A.observedBlendHalfWidth));
}

/**
 * Resort extent: the observed open length fraction when operator status covers at least
 * 60 % of mapped length, otherwise the modelled extent. The switch is a smooth blend over
 * coverage 0.5 to 0.7 (smoothness invariant). `basis` is "observed" from 60 % coverage,
 * otherwise `modelledBasis` ("modelled" by default; the caller passes "prior" when the
 * modelled extent has no evidence behind it). `priorShare` is the share of the extent
 * estimate that comes only from priors, for a smooth prior shrink in extentFactor.
 */
export function observedExtent(args: {
  modelled: number;
  observedOpenFraction: number | null;
  observedCoverage: number;
  modelledBasis?: "modelled" | "prior";
}): { extent: number; basis: ExtentBasis; observedWeight: number; priorShare: number } {
  const modelled = prob(args.modelled);
  const fallback = args.modelledBasis ?? "modelled";
  const obs = args.observedOpenFraction;
  if (!finite(obs)) return { extent: modelled, basis: fallback, observedWeight: 0, priorShare: fallback === "prior" ? 1 : 0 };
  const w = observedExtentWeight(args.observedCoverage);
  const extent = clamp(modelled * (1 - w) + clamp(obs) * w);
  const observed = prob(args.observedCoverage) >= A.observedExtentMinCoverage - A.tolerance;
  return {
    extent,
    basis: observed ? "observed" : fallback,
    observedWeight: w,
    priorShare: fallback === "prior" ? 1 - w : 0,
  };
}

/**
 * Extent factor (owner decision): `openEff = max(E, openKm/60)`, `shut = 1 - openEff`,
 * `X = 1 - 0.25 smoothstep((shut - 0.25)/0.5)`. No penalty up to 25 % shut, 25 % off at
 * 75 % shut or more. When extent comes only from priors the penalty is halved:
 * `X <- 1 - (1 - X) 0.5`. `priorShare` (0 to 1), when given, overrides the basis and
 * shrinks the penalty in proportion (half at 1), so a blended extent stays smooth.
 */
export function extentFactor(args: { extent: number; openKm: number | null; basis: ExtentBasis; priorShare?: number }): number {
  const extent = prob(args.extent);
  const km = finite(args.openKm) ? Math.max(0, args.openKm) : 0;
  const openEff = clamp(Math.max(extent, km / A.extentKmSaturation));
  const shut = 1 - openEff;
  const span = A.extentFullPenaltyShut - A.extentNoPenaltyShut;
  const penalty = A.extentMaxPenalty * smoothstep((shut - A.extentNoPenaltyShut) / span);
  const priorShare = finite(args.priorShare) ? clamp(args.priorShare) : args.basis === "prior" ? 1 : 0;
  const shrink = 1 - (1 - A.extentPriorShrink) * priorShare;
  return 1 - penalty * shrink;
}

// ---------------------------------------------------------------------------
// Day roll-ups (contract Section 14)
// ---------------------------------------------------------------------------

/**
 * Start hour (array index) of the best window of `windowLen` consecutive scored
 * (finite) hours; ties go to the earliest start. When no run of scored hours is that
 * long, the window shrinks to the longest run. Null when no hour is scored.
 */
export function bestWindowStart(hourly: readonly (number | null)[], windowLen: number): number | null {
  const n = hourly.length;
  let longest = 0;
  let run = 0;
  for (let i = 0; i < n; i++) {
    if (scored(hourly[i])) {
      run++;
      if (run > longest) longest = run;
    } else run = 0;
  }
  if (longest === 0) return null;
  const len = Math.min(intLen(windowLen, 1), longest);
  let best = -Infinity;
  let bestStart: number | null = null;
  for (let s = 0; s + len <= n; s++) {
    let sum = 0;
    let ok = true;
    for (let j = s; j < s + len; j++) {
      const v = hourly[j];
      if (!scored(v)) {
        ok = false;
        break;
      }
      sum += v;
    }
    if (!ok) continue;
    if (bestStart === null || sum > best + A.tolerance) {
      best = sum;
      bestStart = s;
    }
  }
  return bestStart;
}

/** Mean of up to `len` consecutive scored hours from `start`, stopping at the first unscored hour. */
function windowMean(hourly: readonly (number | null)[], start: number, len: number): number | null {
  let sum = 0;
  let count = 0;
  for (let j = start; j < hourly.length && count < len; j++) {
    const v = hourly[j];
    if (!scored(v)) break;
    sum += v;
    count++;
  }
  return count > 0 ? sum / count : null;
}

/**
 * `(1 - bestWeight) mean(scored hours) + bestWeight mean(window)`. The window starts at
 * `windowStart` (chosen once on the P50 curve by the caller); when that is null or not a
 * scored hour of this curve, the curve's own best window is used.
 */
function dayRollUp(hourly: readonly (number | null)[], windowStart: number | null, windowLen: number, bestWeight: number): number | null {
  let sum = 0;
  let count = 0;
  for (const v of hourly) {
    if (!scored(v)) continue;
    sum += v;
    count++;
  }
  if (count === 0) return null;
  const mean = sum / count;
  const len = intLen(windowLen, 1);
  let win: number | null = null;
  if (windowStart !== null && Number.isInteger(windowStart) && windowStart >= 0) win = windowMean(hourly, windowStart, len);
  if (win === null) {
    const own = bestWindowStart(hourly, len);
    win = own === null ? mean : windowMean(hourly, own, len) ?? mean;
  }
  return (1 - bestWeight) * mean + bestWeight * win;
}

/**
 * Piste day per member: `0.7 mean(scored hours) + 0.3 mean(best 4 h window)`, the window
 * chosen once on the P50 curve by the caller. Null when no hour is scored (night and
 * hours outside operating time are null and never enter the roll-up).
 */
export function pisteDay(hourly: readonly (number | null)[], windowStart: number | null, windowLen: number = A.pisteWindowH): number | null {
  return dayRollUp(hourly, windowStart, windowLen, A.pisteBestWeight);
}

/** Off-piste day per member: `0.5 mean(scored hours) + 0.5 mean(best 3 h window)`. Null when no hour is scored. */
export function offPisteDay(hourly: readonly (number | null)[], windowStart: number | null, windowLen: number = A.offPisteWindowH): number | null {
  return dayRollUp(hourly, windowStart, windowLen, A.offPisteBestWeight);
}

// ---------------------------------------------------------------------------
// Off-piste per hour (contract Section 14)
// ---------------------------------------------------------------------------

/**
 * Off-piste resort (or band) quality for one hour and member: the mean of the best
 * `topShare` (25 %) of the terrain by area share, with the boundary unit included
 * fractionally. Shares need not sum to 1 (they are used relative to their total; all
 * zero means equal shares). Continuous in scores and shares. `topShare` at or below 0
 * gives the best unit; at or above 1 the share-weighted mean. No valid unit gives 0.
 */
export function offPisteHour(units: readonly { score: number; share: number }[], topShare: number = A.offPisteTopShare): number {
  const valid: { score: number; share: number }[] = [];
  let total = 0;
  for (const u of units) {
    if (!finite(u.score)) continue;
    total += Math.max(0, orElse(u.share, 0));
  }
  // Units with no share contribute nothing to the top-share mean (they are kept only when every
  // share is zero, or for the best-unit case), so they are skipped before sorting.
  const skipEmpty = total > 0 && orElse(topShare, A.offPisteTopShare) > 0;
  for (const u of units) {
    if (!finite(u.score)) continue;
    const share = Math.max(0, orElse(u.share, 0));
    if (skipEmpty && !(share > 0)) continue;
    valid.push({ score: u.score, share });
  }
  if (!valid.length) return 0;
  if (!(total > 0)) {
    for (const v of valid) v.share = 1;
    total = valid.length;
  }
  // Stable sort, best first.
  valid.sort((x, y) => y.score - x.score);
  const top = orElse(topShare, A.offPisteTopShare);
  if (!(top > 0)) return valid[0].score;
  const target = Math.min(1, top) * total;
  let acc = 0;
  let sum = 0;
  for (const v of valid) {
    if (acc >= target) break;
    const take = Math.min(v.share, target - acc);
    if (!(take > 0)) continue;
    sum += v.score * take;
    acc += take;
  }
  return acc > 0 ? sum / acc : valid[0].score;
}

// ---------------------------------------------------------------------------
// Quantiles, status, flags
// ---------------------------------------------------------------------------

/**
 * Continuous weighted quantile of member values (tuned round 4): the order-statistic quantile
 * averaged over the window [q - h, q + h] of cumulative weight (h = memberQuantileWindow, the window
 * kept inside [0, 1]). math.weightedQuantile places each member at the mid-point of its own weight
 * in sorted order, so two members of unequal weight swapping order made the result jump (smoothness
 * and saturation invariants); the window average is continuous in every value and weight. For the
 * standard ensemble in Z order, P50 is exactly the central member, as before. NaN when no member
 * has a finite value and a positive weight.
 */
export function memberQuantile(values: readonly number[], weights: readonly number[], q: number): number {
  const pairs: [number, number][] = [];
  let total = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    const w = weights[i] ?? 0;
    if (!finite(v) || !(w > 0)) continue;
    pairs.push([v, w]);
    total += w;
  }
  if (!pairs.length || !(total > 0)) return NaN;
  pairs.sort((a, b) => a[0] - b[0]);
  const h = Math.min(0.5, Math.max(1e-6, A.memberQuantileWindow));
  const lo = clamp(clamp(q) - h, 0, 1 - 2 * h);
  const hi = lo + 2 * h;
  // Accumulated relative to the first member in the window, so identical members return their
  // value exactly.
  let cum = 0;
  let base = NaN;
  let sum = 0;
  let mass = 0;
  for (const [v, w] of pairs) {
    const a = cum / total;
    cum += w;
    const b = cum / total;
    const overlap = Math.min(b, hi) - Math.max(a, lo);
    if (!(overlap > 0)) continue;
    if (!finite(base)) base = v;
    sum += (v - base) * overlap;
    mass += overlap;
  }
  return finite(base) ? base + (mass > 0 ? sum / mass : 0) : pairs[pairs.length - 1][0];
}

/**
 * Weighted P10, P50 and P90 of member values (memberQuantile). NaN for all three when no member
 * has a finite value and a positive weight; callers publish a null score in that case.
 */
export function memberQuantiles(values: readonly number[], weights: readonly number[]): { p10: number; p50: number; p90: number } {
  return {
    p10: memberQuantile(values, weights, 0.1),
    p50: memberQuantile(values, weights, 0.5),
    p90: memberQuantile(values, weights, 0.9),
  };
}

/** Resort status from the operating probability: closed below 0.2, open above 0.8, otherwise unknown. */
export function resortStatus(piOp: number): SkiStatus {
  if (!finite(piOp)) return "unknown";
  if (piOp < A.closedOperatingProbability) return "closed";
  if (piOp > A.openOperatingProbability) return "open";
  return "unknown";
}

/** Weighted member probability that skiability G is below the cover-risk threshold (0.5). 0 with no valid member. */
export function lowCoverProbability(gMembers: readonly number[], weights: readonly number[]): number {
  let total = 0;
  let low = 0;
  for (let i = 0; i < gMembers.length; i++) {
    const g = gMembers[i];
    const w = weights[i];
    if (!finite(g) || !finite(w) || !(w > 0)) continue;
    total += w;
    if (g < A.coverRiskG) low += w;
  }
  return total > 0 ? low / total : 0;
}

/** Cover-risk flag (contract Section 16): P(G < 0.5) >= 0.2 across weighted members. */
export function coverRisk(gMembers: readonly number[], weights: readonly number[]): boolean {
  return lowCoverProbability(gMembers, weights) >= A.coverRiskProbability - A.tolerance;
}

/**
 * Probability mass of the ice-class states (ICE_RISK_STATES) in a state list. Duplicate
 * names are summed, so a caller can pass a concatenation of already weighted lists
 * (grooming schedules A and B). Clamped to [0, 1].
 */
export function iceStateProbability(stateProbs: readonly StateProbability[]): number {
  let p = 0;
  for (const s of stateProbs) {
    if (ICE_RISK_SET.has(s.state)) p += Math.max(0, orElse(s.p, 0));
  }
  return clamp(p);
}

/** Ice-risk flag (contract Section 16): P(ice, glaze or boilerplate states) >= 0.3. */
export function iceRisk(stateProbs: readonly StateProbability[]): boolean {
  return iceStateProbability(stateProbs) >= A.iceRiskProbability - A.tolerance;
}

// ---------------------------------------------------------------------------
// Labels (contract Section 14)
// ---------------------------------------------------------------------------

/**
 * Piste vocabulary on a 0 to 100 score: Hero Snow, Firing, Great, Good, Challenging,
 * Miserable at 9 / 7.5 / 6.5 / 5 / 2 on the 0 to 10 scale (lib/conditions/piste-labels.ts,
 * reused so the two cannot drift). A non-finite score reads "Score unavailable".
 */
export function pisteLabel(score100: number): string {
  if (!finite(score100)) return SKI_LABEL_UNAVAILABLE;
  return pisteLabel10(score100 / 10);
}

/**
 * Natural vocabulary on a 0 to 100 score: Hero, Firing, Great, Good, Fair, Poor, Flat at
 * 9 / 7.5 / 6.5 / 5 / 3.5 / 2 on the 0 to 10 scale (thresholds copied from the v5
 * off-piste labels). A non-finite score reads "Score unavailable".
 */
export function naturalLabel(score100: number): string {
  if (!finite(score100)) return SKI_LABEL_UNAVAILABLE;
  const s10 = score100 / 10;
  for (const [threshold, label] of A.naturalLabels) {
    if (s10 >= threshold) return label;
  }
  return A.naturalFloorLabel;
}

/**
 * Published label for a sport: "Closed" when the status is closed; "No lift access" when
 * the status is no_lift_access on piste or off-piste (touring needs no lifts and keeps
 * its natural label); "Score unavailable" for a missing score; otherwise piste uses the
 * piste vocabulary and off-piste and touring the natural vocabulary.
 */
export function skiScoreLabel(sport: SkiSport, score100: number | null, status: SkiStatus): string {
  if (status === "closed") return SKI_LABEL_CLOSED;
  if (status === "no_lift_access" && sport !== "touring") return SKI_LABEL_NO_LIFT_ACCESS;
  if (!finite(score100)) return SKI_LABEL_UNAVAILABLE;
  return sport === "piste" ? pisteLabel(score100) : naturalLabel(score100);
}
